import { Box, Center, Divider, Fade, Flex, HStack, SlideFade, Spacer, Spinner, Stack } from '@chakra-ui/react';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';

import { Button } from '../../../design-system/button';
import { useMenuSize } from '../../../design-system/hooks';
import { ArrowLeftOutline } from '../../../design-system/icon';
import { Heading } from '../../../design-system/typography';
import { rem } from '../../../design-system/utils';
import { useLocalStorage } from '../../../lib/storage';
import { useCurrentCompany } from '../../../lib/who-am-i';
import { DimensionKey } from '../../../queries/recognition-analysis';
import { Reveal } from '../../display/reveal';
import { ErrorBoundary } from '../../error/error-boundary';
import { ReactComponent as AiSparkle } from './ai-sparkle.svg';
import { CupFiller } from './cup-filler';
import {
	CompanyValues,
	Dimension,
	DimensionBox,
	DimensionContext,
	DimensionExamples,
	ScoreboxFeedback,
	useDimensionAccordion,
	useScoreboxState,
} from './scorebox-components';
import { SlidingText } from './sliding-text';
import { calculatePercentageStop } from './utils';

type ScoreBoxPopoutProps = {
	dimensions: Dimension[];
	isLoading: boolean;
	text: string;
	onOpenChange?: (isOpen: boolean) => void;
};

export const ScoreBoxPopout: FC<ScoreBoxPopoutProps> = ({ dimensions, isLoading, text, onOpenChange }) => {
	const { t } = useTranslation('recognition', { keyPrefix: 'gratitudeGauge' });
	const [isOpen, setIsOpen] = useLocalStorage('gratitude-gauge-open:desktop', true);
	const matches = dimensions.filter((d) => d.matches).length;
	const percentage = calculatePercentageStop(matches, dimensions.length);
	const ref = useMenuSize();
	const state = useScoreboxState();
	const hasCoreValues = useCurrentCompany().core_values.length > 0;
	const { track } = useAnalytics();
	const { register } = useDimensionAccordion();

	const handleToggle = () => {
		const nextIsOpen = !isOpen;

		if (nextIsOpen) {
			track(AnalyticsEvent.GratitudeGauge.Opened, { type: 'popout' });
		} else {
			track(AnalyticsEvent.GratitudeGauge.Closed, { type: 'popout' });
		}

		setIsOpen(nextIsOpen);
		onOpenChange?.(nextIsOpen);
	};

	return (
		<ErrorBoundary>
			<DimensionContext.Provider value={{ stage: state.stage }}>
				<Flex direction="row" position="absolute" left="calc(100% - 1rem)" columnGap={2} pointerEvents="none">
					<Center
						aria-label={t('buttonLabel')}
						as="button"
						bgColor={isOpen ? 'purple.400' : 'purple.300'}
						border="2px solid"
						borderColor="bg.surface"
						borderRadius="full"
						boxSize={rem(40)}
						flexShrink={0}
						pointerEvents="auto"
						onClick={handleToggle}
						p={1}
						transitionDuration="normal"
						transitionProperty="background-color"
						_hover={{
							bgColor: 'purple.500',
						}}
					>
						<AiSparkle />
					</Center>

					<SlideFade in={isOpen} style={{ pointerEvents: isOpen ? 'auto' : 'none' }}>
						<Box w={rem(322)} bg="bg.surface" borderRadius="lg" overflow="hidden" columnGap={4} overflowY="auto" ref={ref}>
							<Flex
								transform={state.isViewing ? `translateX(calc(-100% - ${rem(16)}))` : 'translateX(0)'}
								transition="transform 0.3s cubic-bezier(0.68, -0.3, 0.265, 1.25)"
								columnGap={4}
								w="full"
							>
								<Box w="full" flexShrink={0}>
									<Reveal if={!state.isViewing || state.isTransitioning}>
										<HStack columnGap={2} p={4}>
											<Heading color="text.default">{t('heading')}</Heading>
											<Spacer />

											<Fade in={isLoading}>
												<Spinner size="sm" color="purple.300" />
											</Fade>
										</HStack>

										<Divider />

										<Stack rowGap={2} p={4}>
											<HStack mb={2} columnGap={2}>
												<CupFiller flexShrink={0} percentage={percentage} />
												<SlidingText text={t('fillText', { context: percentage })} />
											</HStack>

											{dimensions.map((dimension, index) => {
												const registered = register(index);
												return (
													<DimensionBox
														dimension={dimension}
														key={dimension.key}
														onClick={() => state.onViewDimension(dimension)}
														text={text}
														isOpen={registered.isOpen}
														onToggle={registered.onToggle}
													/>
												);
											})}
										</Stack>
									</Reveal>
								</Box>

								<Box w="full" flexShrink={0}>
									<Reveal if={state.isViewing || state.isTransitioning}>
										<HStack columnGap={1} p={4}>
											<Box as="button" px={2} onClick={state.onCloseDimension} color="text.default">
												<ArrowLeftOutline size="xs" />
											</Box>
											<Heading color="text.default">{t('exampleTitle', { context: state.dimension?.key })}</Heading>
										</HStack>

										<Stack rowGap={2} p={4}>
											{state.dimension && (state.dimension.key !== DimensionKey.ValuesAligned || !hasCoreValues) && (
												<DimensionExamples dimension={state.dimension} text={text} />
											)}
											{state.dimension && state.dimension.key === DimensionKey.ValuesAligned && hasCoreValues && <CompanyValues />}

											<Button colorScheme="secondary" variant="alpha" mt={4} onClick={state.onCloseDimension}>
												{t('back')}
											</Button>
										</Stack>
									</Reveal>
								</Box>
							</Flex>

							<ScoreboxFeedback dimensions={dimensions} px={4} pb={4} text={text} />
						</Box>
					</SlideFade>
				</Flex>
			</DimensionContext.Provider>
		</ErrorBoundary>
	);
};
